<template>
  <div class="alice-an alice-rb">
    <header class="alice-an__head">
      <div class="alice-an__head-main">
        <div class="alice-an__crumb">
          <span>Admin</span>
          <Icon name="chevron-right" :size="12" />
          <span>Rulebooks</span>
        </div>
        <h1 class="alice-an__title">
          Rulebooks
          <span v-if="rows.length" class="alice-an__count">{{
            rows.length
          }}</span>
        </h1>
        <p class="alice-an__subtitle">
          Read-only. Each researcher's standing preferences, seeded from the
          chat analysis and learned from their corrections. Researchers manage
          their own rules on their profile page.
        </p>
      </div>
    </header>

    <div v-if="loading" class="alice-an__state">Loading rulebooks…</div>
    <div v-else-if="error" class="alice-an__state alice-an__state--error">
      {{ error }}
    </div>
    <div v-else class="alice-an__body alice-rb__body">
      <div class="alice-an__kpis">
        <div v-for="(tile, i) in kpis" :key="tile.label" class="alice-an__kpi">
          <div class="alice-an__kpi-row">
            <span class="alice-an__kpi-value">{{ tile.value }}</span>
          </div>
          <div class="alice-an__kpi-label">{{ tile.label }}</div>
          <div class="alice-an__kpi-sub">{{ tile.sub }}</div>
          <span
            v-if="i < kpis.length - 1"
            class="alice-an__kpi-divider"
            aria-hidden="true"
          />
        </div>
      </div>

      <div class="alice-rb__split">
        <ul class="alice-rb__people">
          <li
            v-for="r in rows"
            :key="r.uid"
            class="alice-rb__person"
            :class="{ 'alice-rb__person--active': selected === r.uid }"
            @click="selected = r.uid"
          >
            <div class="alice-rb__person-name">
              {{ r.name || r.email || r.uid }}
              <span v-if="!r.enabled" class="alice-rb__off">off</span>
            </div>
            <div class="alice-rb__person-sub">
              {{ r.counts.active }} active · {{ r.counts.learned }} learned ·
              {{ r.counts.seed }} seeded
            </div>
          </li>
          <li v-if="!rows.length" class="alice-an__empty">No rulebooks yet.</li>
        </ul>

        <div class="alice-rb__detail">
          <template v-if="current">
            <div class="alice-rb__detail-head">
              <div class="alice-rb__detail-name">
                {{ current.name || current.email }}
              </div>
              <div class="alice-rb__detail-sub">
                {{ current.email }} · last learned
                {{
                  current.lastLearnedAt ? fmt(current.lastLearnedAt) : "never"
                }}
                · updated {{ current.updatedAt ? fmt(current.updatedAt) : "—" }}
              </div>
            </div>
            <div
              v-for="g in groups(current)"
              :key="g.status"
              class="alice-rb__group"
            >
              <div class="alice-rb__group-title">
                {{ g.label }}
                <span class="alice-an__muted">{{ g.rules.length }}</span>
              </div>
              <ul class="alice-rb__rules">
                <li
                  v-for="rule in g.rules"
                  :key="rule.id"
                  class="alice-rb__rule"
                >
                  <div class="alice-rb__rule-main">
                    <span class="alice-rb__cat">{{ rule.category }}</span>
                    <span class="alice-rb__text">{{ rule.text }}</span>
                  </div>
                  <div class="alice-rb__rule-meta">
                    {{ rule.source }} · confidence
                    {{ Math.round((rule.confidence || 0) * 100) }}% · ×{{
                      rule.hits || 0
                    }}
                    <template v-if="rule.createdAt">
                      · {{ fmt(rule.createdAt) }}</template
                    >
                  </div>
                  <div
                    v-for="(ev, i) in (rule.evidence || [])
                      .filter((e) => e && e.quote)
                      .slice(0, 2)"
                    :key="i"
                    class="alice-rb__evidence"
                  >
                    “{{ ev.quote }}”
                    <span v-if="ev.projectId" class="alice-an__muted">
                      · {{ projectTitle(ev.projectId) }}
                    </span>
                  </div>
                </li>
              </ul>
            </div>
          </template>
          <div v-else class="alice-an__empty">Select a researcher.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/firebase";
import Icon from "@/components/alice/Icon.vue";

const STATUS_GROUPS = [
  { status: "active", label: "Active" },
  { status: "suggested", label: "Suggested" },
  { status: "retired", label: "Paused / retired" },
  { status: "dismissed", label: "Dismissed" },
];

export default {
  name: "AdminRulebooks",
  components: { Icon },
  data() {
    return { rows: [], loading: true, error: "", selected: null };
  },
  computed: {
    ...mapState({ projects: (state) => state.projects || [] }),
    current() {
      return this.rows.find((r) => r.uid === this.selected) || null;
    },
    kpis() {
      const sum = (f) => this.rows.reduce((n, r) => n + f(r), 0);
      const month = Date.now() - 30 * 86400000;
      return [
        {
          label: "Researchers",
          value: this.rows.length,
          sub: "with a rulebook",
        },
        {
          label: "Active rules",
          value: sum((r) => r.counts.active),
          sub: "applied in chat",
        },
        {
          label: "Learned (30d)",
          value: sum(
            (r) =>
              r.rules.filter(
                (x) => x.source === "learned" && (x.createdAt || 0) > month
              ).length
          ),
          sub: "from corrections",
        },
        {
          label: "Dismissed",
          value: sum((r) => r.counts.dismissed),
          sub: "undone by researchers",
        },
      ];
    },
  },
  async mounted() {
    try {
      const fn = httpsCallable(functions, "getRulebooks");
      const res = await fn({});
      if (res.data && res.data.error === 403) {
        this.error = "No admin access.";
      } else {
        this.rows = (res.data && res.data.rows) || [];
        this.selected = this.rows.length ? this.rows[0].uid : null;
      }
    } catch (e) {
      this.error = e.message || String(e);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    groups(row) {
      return STATUS_GROUPS.map((g) => ({
        ...g,
        rules: (row.rules || []).filter((r) => r.status === g.status),
      })).filter((g) => g.rules.length);
    },
    projectTitle(uid) {
      const p = this.projects.find((x) => x.uid === uid);
      return p ? p.title : uid;
    },
    fmt(ms) {
      return new Date(Number(ms)).toLocaleDateString("nl-NL", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
  },
};
</script>

<style scoped lang="scss">
.alice-rb {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  background: var(--vp-white);
  font-family: var(--font-body);
  color: var(--fg-1);
}
.alice-an__head {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 32px 32px 24px;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-an__crumb {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}
.alice-an__title {
  margin: 8px 0 0;
  font-family: var(--font-display);
  font-size: 28px;
  display: flex;
  align-items: baseline;
  gap: 12px;
}
.alice-an__count {
  font-size: 14px;
  color: var(--fg-3);
}
.alice-an__subtitle {
  margin: 8px 0 0;
  font-size: 16px;
  color: var(--fg-2);
  max-width: 620px;
  line-height: 1.5;
}
.alice-an__state {
  padding: 32px;
  color: var(--fg-2);
}
.alice-an__state--error {
  color: var(--vp-red, #c00);
}
.alice-rb__body {
  padding: 24px 32px 48px;
}
.alice-an__kpis {
  display: flex;
  gap: 32px;
  margin-bottom: 28px;
}
.alice-an__kpi {
  position: relative;
  min-width: 140px;
}
.alice-an__kpi-value {
  font-family: var(--font-display);
  font-size: 28px;
}
.alice-an__kpi-label {
  font-family: var(--font-ui);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--fg-2);
}
.alice-an__kpi-sub {
  font-size: 13px;
  color: var(--fg-3);
}
.alice-rb__split {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 32px;
}
.alice-rb__people {
  list-style: none;
  margin: 0;
  padding: 0;
  border-top: 1px solid var(--border-hairline);
}
.alice-rb__person {
  padding: 12px 10px;
  border-bottom: 1px solid var(--border-hairline);
  cursor: pointer;
}
.alice-rb__person:hover,
.alice-rb__person--active {
  background: var(--bg-muted);
}
.alice-rb__person-name {
  font-size: 15px;
  display: flex;
  gap: 8px;
  align-items: baseline;
}
.alice-rb__off {
  font-family: var(--font-ui);
  font-size: 10px;
  text-transform: uppercase;
  color: var(--fg-3);
}
.alice-rb__person-sub,
.alice-rb__detail-sub,
.alice-rb__rule-meta,
.alice-an__muted {
  font-size: 12px;
  color: var(--fg-3);
}
.alice-rb__detail-name {
  font-family: var(--font-display);
  font-size: 20px;
}
.alice-rb__group {
  margin-top: 22px;
}
.alice-rb__group-title {
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-2);
  margin-bottom: 8px;
}
.alice-rb__rules {
  list-style: none;
  margin: 0;
  padding: 0;
  border-top: 1px solid var(--border-hairline);
}
.alice-rb__rule {
  padding: 10px 0;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-rb__rule-main {
  display: flex;
  gap: 12px;
  align-items: baseline;
}
.alice-rb__cat {
  font-family: var(--font-ui);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--fg-3);
  min-width: 64px;
}
.alice-rb__text {
  font-size: 15px;
}
.alice-rb__evidence {
  margin-top: 4px;
  padding-left: 76px;
  font-size: 13px;
  color: var(--fg-2);
}
.alice-an__empty {
  padding: 16px 0;
  color: var(--fg-3);
}
</style>
